import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import API_URL from './config';
import './LeagueLeaders.css';

/**
 * LeagueLeaders Component
 * 
 * Displays league leaders for various statistical categories (points, assists, rebounds, etc).
 * Fetches from the dedicated playerSeasonStats collection for fast results.
 */

const STAT_CATEGORIES = [
  { key: 'points', label: 'Scoring Leaders', abbr: 'PPG' },
  { key: 'assists', label: 'Assists Leaders', abbr: 'APG' },
  { key: 'rebounds', label: 'Rebounds Leaders', abbr: 'RPG' },
  { key: 'steals', label: 'Steals Leaders', abbr: 'SPG' },
  { key: 'blocks', label: 'Blocks Leaders', abbr: 'BPG' },
  { key: 'turnovers', label: 'Turnovers Leaders', abbr: 'TPG' },
];

export default function LeagueLeaders() {
  const [selectedStat, setSelectedStat] = useState('points');
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(10);
  const [saveId, setSaveId] = useState(null);
  const [season, setSeason] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  // Load active save and season on component mount
  useEffect(() => {
    const activeSaveId = localStorage.getItem("activeSaveId");
    if (!activeSaveId) {
      setError("No active save found. Please select a save.");
      setPageLoading(false);
      return;
    }

    const fetchSaveData = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const userId = user._id;
        
        const response = await fetch(`${API_URL}/api/saves/${activeSaveId}`, {
          headers: { "X-User-ID": userId },
        });
        if (!response.ok) throw new Error("Failed to fetch save data");
        const saveData = await response.json();
        setSaveId(activeSaveId);
        setSeason(saveData.save?.currentSeason || 1);
      } catch (err) {
        setError("Failed to load save data");
        // Error loading data
      } finally {
        setPageLoading(false);
      }
    };

    fetchSaveData();
  }, []);

  // Fetch league leaders whenever filters or save data changes
  useEffect(() => {
    if (saveId && season !== null) {
      fetchLeagueLeaders();
    }
  }, [selectedStat, limit, saveId, season]);

  const fetchLeagueLeaders = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        stat: selectedStat,
        limit: limit,
      });

      if (saveId) params.append('save_id', saveId);
      if (season !== null) params.append('season', season);

      const response = await fetch(`${API_URL}/api/stats/league-leaders?${params}`);

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorMessage = 'Failed to fetch league leaders';
        
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorMessage;
          } catch (e) {
            errorMessage = `Server error (${response.status})`;
          }
        } else {
          errorMessage = `Server error (${response.status})`;
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setLeaders(data.leagueLeaders || []);
    } catch (err) {
      setError(err.message || 'An error occurred');
      setLeaders([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatCategory = (stat) => {
    return STAT_CATEGORIES.find((c) => c.key === stat) || { label: stat, abbr: stat };
  };

  const getStatValue = (player, stat) => {
    const key = `avg${stat.charAt(0).toUpperCase()}${stat.slice(1)}`;
    const avgKey = `stats.${stat}.avg`;
    const value = player[key] || player[avgKey];
    return value ? parseFloat(value).toFixed(1) : '0.0';
  };

  const category = getStatCategory(selectedStat);

  return (
    <>
      <Navbar />
      <main className="league-leaders-container">
        <h1>League Leaders</h1>

        {pageLoading && <div className="loading-message">Loading league data...</div>}

        {!pageLoading && (
        <>
        {/* Filters */}
        <div className="league-leaders-filters">
          <div className="filter-group">
            <label htmlFor="stat-select">Category:</label>
            <select
              id="stat-select"
              value={selectedStat}
              onChange={(e) => setSelectedStat(e.target.value)}
              className="filter-select"
            >
              {STAT_CATEGORIES.map((category) => (
                <option key={category.key} value={category.key}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="limit-select">Show Top:</label>
            <select
              id="limit-select"
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value))}
              className="filter-select"
            >
              <option value={5}>5 Players</option>
              <option value={10}>10 Players</option>
              <option value={25}>25 Players</option>
              <option value={50}>50 Players</option>
            </select>
          </div>
        </div>

        {/* Error Message */}
        {error && <div className="error-message">{error}</div>}

        {/* Loading State */}
        {loading && <div className="loading-message">Loading league leaders...</div>}

        {/* Leaders Table */}
        {!loading && leaders.length > 0 && (
          <div className="leaders-table-wrapper">
            <div className="stat-title">{category.label}</div>
            <table className="leaders-table">
              <thead>
                <tr>
                  <th className="rank-column">Rank</th>
                  <th className="name-column">Player</th>
                  <th className="position-column">Pos</th>
                  <th className="gp-column">GP</th>
                  <th className="mpg-column">MPG</th>
                  <th className="stat-column">{category.abbr}</th>
                </tr>
              </thead>
              <tbody>
                {leaders.map((player, index) => {
                  const playerDisplayName = player.playerName || player.displayName || player.name || 'Unknown';
                  const playerStatValue = player.stats?.[selectedStat]?.avg 
                    ? parseFloat(player.stats[selectedStat].avg).toFixed(1) 
                    : '0.0';
                  const mpgValue = player.stats?.minutes?.total / (player.gamesPlayed || 1);
                  
                  return (
                    <tr key={player._id || index} className="leader-row">
                      <td className="rank-column">
                        <span className={`rank-badge rank-${index + 1}`}>
                          {index + 1}
                        </span>
                      </td>
                      <td className="name-column">
                        {player.playerId ? (
                          <Link className="player-name" to={`/players/${player.playerId}`}>
                            {playerDisplayName}
                          </Link>
                        ) : (
                          <span className="player-name">{playerDisplayName}</span>
                        )}
                      </td>
                      <td className="position-column">{player.position || '-'}</td>
                      <td className="gp-column">{player.gamesPlayed}</td>
                      <td className="mpg-column">{mpgValue?.toFixed(1) || '0.0'}</td>
                      <td className="stat-column highlight">
                        <strong>{playerStatValue}</strong>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty State */}
        {!loading && leaders.length === 0 && !error && (
          <div className="empty-state">
            <p>No statistics available. Play some games to see league leaders!</p>
          </div>
        )}
        </>
        )}
      </main>
    </>
  );
}
